import React from "react";

import {
  FetchNotFoundError,
  FetchServerError,
  FetchUnauthorizedError,
  FetchPasswordInvalidError,
  FetchSsoError,
  FetchElevatedAccessError,
} from "@lib/errors";
import { FetchUserError } from "@lib/errors/FetchUserError";
import { UNIDENTIFIED_ERROR } from "@lib/fetch";

import useI18nNamespace from "@services/useI18nNamespace";

import { getBrazeApiDenialCode } from "./utils/getBrazeApiDenialCode";
import type { FetchErrorModalTitleProps } from "./types";

export const FetchErrorModalTitle = ({ error }: FetchErrorModalTitleProps): React.ReactElement => {
  const { t } = useI18nNamespace("server-fetch-error-messages");
  // Braze::API denial codes pick a truthful title before the class branches — see the matching
  // switch in FetchErrorModalBody.
  switch (getBrazeApiDenialCode(error)) {
    case "forbidden.csrf":
      return <>{t("csrf-token-expired-title", "Session Expired")}</>;
    case "forbidden.read_only_impersonation":
      return <>{t("read-only-impersonation-title", "Read-Only Impersonation")}</>;
    case "unauthorized.ip_not_allowed":
    case "unauthorized.company_banned":
      return <>{t("account-restricted-title", "Account Issue")}</>;
    case "sandbox_tos.required":
      return <>{t("sandbox-tos-required-title", "Terms of Service Required")}</>;
    case "service_unavailable.company":
      return <>{t("account-unavailable-title", "Account Temporarily Unavailable")}</>;
  }
  if (error instanceof FetchServerError) {
    switch (error.status) {
      case 503:
      case 504:
        return <>{t("request-timeout-title", "Request Timed Out")}</>;
      case 500:
      default:
        return <>{t("server-error-title", "Unexpected Error")}</>;
    }
  }
  if (error instanceof FetchElevatedAccessError) {
    return <>{t("fetch-elevated-access-error-title", "Reauthentication Required")}</>;
  }
  if (error instanceof FetchUserError) {
    return <>{error.responseJson.warn_header}</>;
  }
  if (error instanceof FetchNotFoundError) {
    return <>{t("fetch-not-found-title", "Page Not Found")}</>;
  }
  if (error instanceof FetchUnauthorizedError) {
    return <>{t("unauthorized-title", "Insufficient Permissions")}</>;
  }
  if (error instanceof FetchPasswordInvalidError) {
    return <>{t("password-expired-or-invalid-title", "Must Change Password")}</>;
  }
  if (error instanceof FetchSsoError) {
    return <>{t("sso-title", "SSO Authentication Now Required")}</>;
  }
  if (error.status === UNIDENTIFIED_ERROR) {
    return <>{t("unidentified-error-title", "Please Refresh Page")}</>;
  }
  return <>{t("server-error-title", "Unexpected Error")}</>;
};
